// All your recipes: carousel + per-cookbook sections, search, Pantry Ready filter,
// select mode with bulk add to planner / mealplan / cookbook and delete.
import React, { useMemo, useState } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useNav } from '../navigation';
import { useDiet, useHeader, useToday } from '../hooks';
import { newId, useLibrary } from '../store/library';
import { usePlanner } from '../store/planner';
import { useShopping } from '../store/shopping';
import { isPantryReady } from '../logic/grocery';
import { exportLibrary } from '../store/backup';
import { exportText } from '../platform/files';
import { copyText } from '../platform/clipboard';
import { CardGrid, Carousel, type CardData } from '../components/Cards';
import SelectionBar, { useSelection } from '../components/SelectionBar';
import { CookbookPickerSheet, MealplanPickerSheet, PlanPickerSheet } from '../components/Pickers';
import { notify, showMenu } from '../components/Dialogs';
import { Caption, EmptyState, Field, IconButton, Row, Screen, Section, Button } from '../components/ui';
import { useCreateMenu } from './LibraryScreen';
import { spacing } from '../theme';
import type { Recipe } from '../types';

type Filter = 'all' | 'pantryReady' | 'favourites';

export function addRecipesToCookbook(cookbookId: string, recipeIds: string[], defaultSection: string): number {
  const { cookbooks, put } = useLibrary.getState();
  const book = cookbooks[cookbookId];
  if (!book) return 0;
  const present = new Set(book.sections.flatMap(s => s.recipeIds));
  const toAdd = recipeIds.filter(id => !present.has(id)); // Swift: recipes already anywhere in the book are skipped
  if (!toAdd.length) return 0;
  const sections = book.sections.length ? [...book.sections] : [{ id: newId(), title: defaultSection, note: '', recipeIds: [] }];
  sections[0] = { ...sections[0], recipeIds: [...sections[0].recipeIds, ...toAdd] };
  put('cookbooks', { ...book, sections });
  return toAdd.length;
}

export default function AllRecipesScreen() {
  const { t } = useTranslation();
  const nav = useNav();
  const today = useToday();
  const recipes = useLibrary(s => s.recipes);
  const cookbooks = useLibrary(s => s.cookbooks);
  const remove = useLibrary(s => s.remove);
  const put = useLibrary(s => s.put);
  const pantry = useShopping(s => s.pantry);
  const plan = usePlanner(s => s.plan);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [sheet, setSheet] = useState<'planner' | 'mealplan' | 'cookbook' | null>(null);
  const sel = useSelection();
  const openCreate = useCreateMenu();
  const diet = useDiet();

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return Object.values(recipes)
      .filter(r => !diet.hidden(r))
      .filter(r => !q || r.title.toLowerCase().includes(q))
      .filter(r => filter !== 'pantryReady' || isPantryReady(r, pantry))
      .sort((a, b) => a.title.localeCompare(b.title, undefined, { numeric: true }));
  }, [recipes, query, filter, pantry, diet]);

  const toCard = (r: Recipe): CardData => ({ id: r.id, title: r.title, subtitle: t('common.minutes', { count: r.durationMin }), media: r.image, icon: 'recipe', diets: diet.issues(r) });
  const ingredientsInPantry = pantry.items.filter(i => i.kind === 'ingredient').length;
  const utensilsInPantry = pantry.items.filter(i => i.kind === 'utensil').length;

  const longPress = (id: string) =>
    showMenu([
      { label: t('common.open'), icon: 'forward', onPress: () => nav.navigate('Recipe', { id }) },
      { label: t('common.copyTitle'), icon: 'copy', onPress: () => copyText(recipes[id]?.title ?? '') },
      { label: t('common.share'), icon: 'share', onPress: () => exportText(`${recipes[id]?.title ?? 'recipe'}.json`, exportLibrary({ recipes: [id] })) },
    ]);

  useHeader(
    {
      title: t('kinds.recipes'),
      headerRight: () =>
        sel.editing ? (
          <Row gap={spacing.m}>
            <IconButton icon="checklist" label={t('common.selectAll')} onPress={() => sel.setAll(shown.map(r => r.id))} />
            <IconButton icon="check" label={t('common.done')} onPress={sel.stop} />
          </Row>
        ) : (
          <Row gap={spacing.m}>
            <IconButton icon="plus" label={t('library.create')} onPress={() => openCreate('recipes')} />
            <IconButton
              icon="more"
              label={t('common.more')}
              onPress={() =>
                showMenu([
                  { label: t('library.filterAll'), icon: filter === 'all' ? 'check' : undefined, onPress: () => setFilter('all') },
                  { label: t('library.filterPantryReady'), icon: filter === 'pantryReady' ? 'check' : 'pantry', onPress: () => setFilter('pantryReady') },
                  { label: t('library.filterFavourites'), icon: 'sparkles', disabled: true, onPress: () => {} },
                  { label: t('common.select'), icon: 'checklist', onPress: sel.start },
                ])
              }
            />
          </Row>
        ),
    },
    [sel.editing, filter, shown, t],
  );

  const selectedIds = [...sel.ids];
  const isSelected = sel.editing ? (id: string) => sel.has(id) : undefined;
  const onPress = (id: string) => (sel.editing ? sel.toggle(id) : nav.navigate('Recipe', { id }));

  return (
    <View style={{ flex: 1 }}>
      <Screen>
        <Field placeholder={t('library.placeholderSearch')} value={query} onChangeText={setQuery} autoCorrect={false} accessibilityLabel={t('common.search')} />
        <Caption>{t('library.pantryCaption', { ingredients: ingredientsInPantry, utensils: utensilsInPantry })}</Caption>
        {shown.length === 0 ? (
          <EmptyState icon="recipe" title={query ? t('library.noResults') : t('common.empty')} action={!query ? <Button label={`${t('library.create')}: ${t('kinds.recipe')}`} icon="plus" onPress={() => openCreate('recipes')} /> : undefined} />
        ) : query || filter !== 'all' ? (
          <CardGrid items={shown.map(toCard)} onPress={onPress} onLongPress={longPress} isSelected={isSelected} />
        ) : (
          <>
            <Carousel items={shown.map(toCard)} onPress={onPress} onLongPress={longPress} isSelected={isSelected} />
            {Object.values(cookbooks)
              .sort((a, b) => a.title.localeCompare(b.title))
              .map(book => {
                const items = book.sections.flatMap(s => s.recipeIds).map(id => recipes[id]).filter((r): r is Recipe => !!r && !diet.hidden(r));
                if (!items.length) return null;
                return (
                  <Section key={book.id} title={book.title} icon="cookbook" right={<IconButton icon="forward" label={book.title} onPress={() => nav.navigate('Cookbook', { id: book.id })} />}>
                    <Carousel items={items.map(toCard)} onPress={onPress} onLongPress={longPress} isSelected={isSelected} />
                  </Section>
                );
              })}
          </>
        )}
      </Screen>
      {sel.editing ? (
        <SelectionBar
          count={sel.ids.size}
          onDelete={() => {
            remove('recipes', selectedIds);
            sel.stop();
          }}
          extra={[
            { label: t('library.addToPlanner'), icon: 'calendarAdd', onPress: () => setSheet('planner') },
            { label: t('library.addToMealplan'), icon: 'mealplan', onPress: () => setSheet('mealplan') },
            { label: t('library.addToCookbook'), icon: 'cookbook', onPress: () => setSheet('cookbook') },
          ]}
        />
      ) : null}
      <PlanPickerSheet
        visible={sheet === 'planner'}
        today={today}
        onClose={() => setSheet(null)}
        onPick={(day, slot) => {
          plan(day, slot, selectedIds);
          setSheet(null);
          sel.stop();
        }}
      />
      <MealplanPickerSheet
        visible={sheet === 'mealplan'}
        onClose={() => setSheet(null)}
        onPick={(mp, day, slot) => {
          put('mealplans', { ...mp, entries: [...mp.entries, ...selectedIds.map(recipeId => ({ day, slot, recipeId }))] });
          setSheet(null);
          sel.stop();
        }}
      />
      <CookbookPickerSheet
        visible={sheet === 'cookbook'}
        onClose={() => setSheet(null)}
        onPick={id => {
          addRecipesToCookbook(id, selectedIds, t('library.defaultSection'));
          notify(t('library.addedToCookbook', { title: cookbooks[id]?.title ?? '' }));
          setSheet(null);
          sel.stop();
        }}
      />
    </View>
  );
}
